import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getFollowupSummary } from '../../services/api';

const statusColors = { good: 'pill-green', needs_attention: 'pill-yellow', critical: 'pill-red', unknown: 'pill-gray' };

// Buckets by months since last visit.
const BUCKETS = [
  { key: 'never',  label: 'Never visited',  color: '#6b7280' },
  { key: 'm1',     label: 'Within last month', color: '#16a34a' },
  { key: 'm1_2',   label: '1–2 months ago', color: '#65a30d' },
  { key: 'm2_3',   label: '2–3 months ago', color: '#d97706' },
  { key: 'm3_6',   label: '3–6 months ago', color: '#ea580c' },
  { key: 'm6',     label: 'Over 6 months',  color: '#dc2626' },
];

const monthsSince = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
};

const bucketOf = (last_date) => {
  if (!last_date) return 'never';
  const m = monthsSince(last_date);
  if (m < 1) return 'm1';
  if (m < 2) return 'm1_2';
  if (m < 3) return 'm2_3';
  if (m < 6) return 'm3_6';
  return 'm6';
};

const FollowupRecords = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [bucket, setBucket] = useState('all');

  useEffect(() => {
    getFollowupSummary().then(r => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const withBucket = rows.map(r => ({ ...r, _bucket: bucketOf(r.last_date) }));
  const counts = BUCKETS.reduce((acc, b) => ({ ...acc, [b.key]: withBucket.filter(r => r._bucket === b.key).length }), {});
  const shown = bucket === 'all' ? withBucket : withBucket.filter(r => r._bucket === bucket);
  const fullName = (m) => [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(' ');

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.followupRecords')}</h1><p>{rows.length} members</p></div>
      </div>

      {/* Bucket cards */}
      <div className="stat-cards" style={{ gridTemplateColumns: `repeat(${BUCKETS.length + 1}, 1fr)`, marginBottom: 20, gap: 10 }}>
        <div onClick={() => setBucket('all')} className="stat-card" style={{ cursor: 'pointer', border: `2px solid ${bucket === 'all' ? '#4f46e5' : 'transparent'}` }}>
          <div><div className="stat-label">All</div><div className="stat-value">{rows.length}</div></div>
        </div>
        {BUCKETS.map(b => (
          <div key={b.key} onClick={() => setBucket(b.key)} className="stat-card" style={{ cursor: 'pointer', border: `2px solid ${bucket === b.key ? b.color : 'transparent'}` }}>
            <div><div className="stat-label" style={{ color: b.color }}>{b.label}</div><div className="stat-value">{counts[b.key]}</div></div>
          </div>
        ))}
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>#</th><th>{t('common.name')}</th><th>{t('members.spiritualFather')}</th>
              <th>Last Visited</th><th>{t('common.status')}</th><th>{t('common.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td colSpan={6}><div className="empty-state"><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : shown.map((r, i) => {
              const b = BUCKETS.find(x => x.key === r._bucket);
              return (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td><strong>{fullName(r)}</strong></td>
                  <td>{(r.father_name || '').trim() || '—'}</td>
                  <td>
                    {r.last_date
                      ? <span>{r.last_date} <span style={{ color: b?.color, fontSize: 11, fontWeight: 600 }}>· {b?.label}</span></span>
                      : <span style={{ color: '#6b7280', fontWeight: 600 }}>Never visited</span>}
                  </td>
                  <td>{r.last_status ? <span className={`pill ${statusColors[r.last_status] || 'pill-gray'}`}>{r.last_status.replace('_', ' ')}</span> : '—'}</td>
                  <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.last_notes || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FollowupRecords;
